import { z, ZodIssueCode } from "zod";
import {
  PaymentMethod,
  Role,
  AdjustmentValueType,
  PaymentSource,
} from "../enums";
import moment, { Moment } from "moment";
const phoneNumberSchema = z
  .string()
  .min(1, "This field is required")
  .min(10, "Invalid number")
  .max(10, "Invalid number")
  .regex(/^[0-9]+$/, "Invalid number");

const USPostalCodeSchema = z
  .string()
  .min(1, "This field is required")
  .regex(/(^\d{5}$)|(^\d{5}-\d{4}$)/, "invalid zip format");

const CanadaPostalCodeSchema = z
  .string()
  .min(1, "This field is required")
  .transform((val) => val.toUpperCase().trim())
  .refine(
    (val) => /^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/.test(val),
    "Invalid postal code format",
  );

const urlSchema = z.union([
  z
    .string()
    .regex(
      /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/,
      "Invalid url",
    ),
  z.literal(""),
]);

export const dateSchema = z.custom<Moment>(
  (value) => moment.isMoment(value) && value.isValid(),
  {
    message: "This field is required",
  },
);

export const loginSchema = z.object({
  username: z.string().trim().min(1, "username is required"),
  password: z
    .string()
    .trim()
    .min(1, "password is required")
    .min(6, "password must be at least 6 characters"),
});

export const createContactSchema = z.object({
  title: z.string().trim().optional().or(z.literal("")),
  name: z.string().trim().min(1, "This field is required"),
  email: z.string().email().min(1, "This field is required"),
  number: phoneNumberSchema,
  isEmailRequired: z
    .string()
    .min(1, "This field is required")
    .refine((value) => ["true", "false"].includes(value), {
      message: "Invalid value",
    }),
  isBillingNotificationsRequired: z
    .string()
    .min(1, "This field is required")
    .refine((value) => ["true", "false"].includes(value), {
      message: "Invalid value",
    }),
  isSmsRequired: z
    .string()
    .min(1, "This field is required")
    .refine((value) => ["true", "false"].includes(value), {
      message: "Invalid value",
    }),
  isLoginRequired: z.string().optional(),
});

export const createFleetSchema = z.object({
  washTypeId: z.number().min(1, { message: "This field is required" }),
  vehicleNumber: z.string().trim().min(1, "This field is required"),
  isPreSelected: z.boolean().optional(),
});

export const getCreateLocationSchema = (isCanada: boolean) => {
  return z.object({
    locationName: z.string().min(1, "This field is required"),
    addressLine1: z.string().min(1, "This field is required"),
    addressLine2: z.string().trim().optional(),
    city: z.string().min(1, "This field is required"),
    state: z.string().min(1, "This field is required"),
    zip: isCanada ? CanadaPostalCodeSchema : USPostalCodeSchema,
    instruction: z.string().optional(),
  });
};

export const editAccountProfileHeaderSchema = z.object({
  accountName: z.string().trim().min(1, "This field is required"),
  logo: z.any().optional(),
});

export const getEditAccountProfileBodySchema = (isCanada: boolean) => {
  return z.object({
    contactPerson: z.string().trim().min(1, "This field is required"),
    email: z.string().email().min(1, "This field is required"),
    contactNumber: z.string().min(1, "This field is required"),
    website: urlSchema,
    addressLine1: z.string().trim().min(1, "This field is required"),
    addressLine2: z.string().trim().optional(),
    city: z.string().trim().min(1, "This field is required"),
    state: z.string().min(1, "This field is required"),
    zip: isCanada ? CanadaPostalCodeSchema : USPostalCodeSchema,
    instruction: z.string().trim().min(1, "This field is required"),
  });
};

export const OneOffAddNewAccountSchema = z.object({
  companyName: z.string().optional(),
  contactPerson: z.string().trim().min(1, "This field is required"),
  email: z.string().email().optional(),
  contactNumber: z.union([phoneNumberSchema, z.literal("")]).optional(),
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z
    .string()
    .optional()
    .refine(
      (val) => !val || /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val),
      "invalid zip format",
    ),
});

export const editUserProfileBodySchema = z.object({
  firstName: z.string().min(1, "This field is required"),
  lastName: z.string().min(1, "This field is required"),
  emailId: z.string().email().min(1, "This field is required"),
  phoneNumber: z.string().min(1, "This field is required"),
  roleCode: z.string().min(1, "This field is required"),
  orgId: z.number(),
});

export const createUserProfileBodySchema = z
  .object({
    firstName: z.string().min(1, "This field is required"),
    lastName: z.string().min(1, "This field is required"),
    emailId: z.string().or(z.literal("")),
    phoneNumber: z.string().min(1, "This field is required"),
    roleCode: z.string().min(1, "This field is required"),
    orgId: z.union([
      z.number({ invalid_type_error: "This field is required" }),
      z.string(),
    ]),
    active: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (
      data.roleCode !== Role.Lead &&
      (!data.emailId || !z.string().email().safeParse(data.emailId).success)
    ) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ["emailId"],
        message: "A valid email is required for this role",
      });
    }
  });

export const getCreateWashTypeBodySchema = (isAdjustments?: boolean) => {
  return z.object({
    name: z.string().min(1, "This field is required"),
    description: z.string().min(1, "This field is required"),
    unitOfMeasurement: isAdjustments
      ? z.string().optional()
      : z.string().min(1, "This field is required"),
    orgId: z.union([
      z.number({ invalid_type_error: "This field is required" }),
      z.string(),
    ]),
    code: z.string().min(1, "This field is required"),
    rate: z.union([
      z.number({ invalid_type_error: "This field is required" }),
      z.string().min(1, "This field is required"),
    ]),
    chargeType: isAdjustments
      ? z.string().min(1, "This field is required")
      : z.string().optional(),
  });
};

export const editWashInventorySchema = z.object({
  endTime: dateSchema,
  washTypeId: z.number().min(1, "This field is required"),
  leadId: z.number().min(1, "This field is required"),
  vehicleNumber: z.string().trim().min(1, "This field is required"),
});

export const washInventorySchema = z
  .object({
    accountId: z.number().min(1, "This field is required"),
    locationNumber: z.number().min(1, "This field is required"),
    endTime: dateSchema,
    washTypeId: z.number().min(1, "This field is required"),
    unitOfMeasurement: z.string().trim().min(1, "This field is required"),
    leadId: z.number().min(1, "This field is required"),
    vehicleNumbers: z.string().array().optional(),
    hours: z.string().optional(),
  })
  .refine((data) => !(data.unitOfMeasurement === "hours" && !data.hours), {
    message: "This field is required",
    path: ["hours"],
  })
  .refine(
    (data) =>
      !(
        data.unitOfMeasurement === "quantity" &&
        (!data.vehicleNumbers || data.vehicleNumbers.length === 0)
      ),
    {
      message: "This field is required",
      path: ["vehicleNumbers"],
    },
  );

export const bulkEditWashInventorySchema = z
  .object({
    endTime: dateSchema,
    washTypeId: z.number().optional(),
    leadId: z.number().optional(),
    vehicleNumber: z.string().trim().optional(),
  })
  .refine((data) => !(data.endTime && data.washTypeId && data.vehicleNumber), {
    message: "",
    path: ["endTime"],
  })
  .refine((data) => !(data.endTime && data.washTypeId && data.vehicleNumber), {
    message: "",
    path: ["washTypeId"],
  })
  .refine((data) => !(data.endTime && data.washTypeId && data.vehicleNumber), {
    message: "You can't update Date, Fleet Number, and Wash Type together",
    path: ["vehicleNumber"],
  });

export const editWashChargeSchema = z
  .object({
    washTypeId: z.number({
      required_error: "This field is required",
      invalid_type_error: "This field is required",
    }),
    washRate: z
      .string()
      .min(1, "This field is required")
      .refine((value) => !value || /^\d+(\.\d+)?$/.test(value), {
        message: "Invalid",
      }),
    frequency: z.string().min(1, "This field is required"),
    customFrequencyDays: z.string().optional(),
    instructions: z.string(),
  })
  .refine((data) => data.frequency !== "custom" || !!data.customFrequencyDays, {
    message: "This field is required",
    path: ["customFrequencyDays"],
  })
  .refine(
    (data) =>
      data.frequency !== "custom" ||
      /^\d+$/.test(data.customFrequencyDays || ""),
    {
      message: "Invalid",
      path: ["customFrequencyDays"],
    },
  )
  .refine(
    (data) =>
      data.frequency !== "custom" || Number(data.customFrequencyDays) > 0,
    {
      message: "Must be greater than 0",
      path: ["customFrequencyDays"],
    },
  );

export const editMiscChargeSchema = z.object({
  washTypeId: z.number({
    required_error: "This field is required",
    invalid_type_error: "This field is required",
  }),
  washRate: z
    .string()
    .min(1, "This field is required")
    .refine((value) => !value || /^\d+(\.\d+)?$/.test(value), {
      message: "Invalid",
    }),
});

export const adjustmentChargeSchema = z
  .object({
    washTypeId: z.number({
      required_error: "This field is required",
      invalid_type_error: "This field is required",
    }),

    washRate: z
      .string()
      .min(1, "This field is required")
      .refine((val) => !isNaN(Number(val)), {
        message: "Enter a valid number",
      }),

    valueType: z.nativeEnum(AdjustmentValueType),
  })
  .refine(
    (data) =>
      data.valueType !== AdjustmentValueType.Percentage ||
      !data.washRate.includes(".") ||
      data.washRate.split(".")[1].length <= 2,
    {
      message: "Percentage can have up to 2 decimal places",
      path: ["washRate"],
    },
  )

  .refine(
    (data) =>
      data.valueType !== AdjustmentValueType.Flat ||
      !data.washRate.includes(".") ||
      data.washRate.split(".")[1].length <= 2,
    {
      message: "Enter valid amount with up to 2 decimals",
      path: ["washRate"],
    },
  );

export const editBillingCycleSchema = z.object({
  proposedInvoiceFrequency: z.string().min(1, "This field is required"),
  proposedInvoiceDate: z.any(),
});

const passwordSchema = z
  .string()
  .min(8, { message: "Password must be at least 8 characters long." })
  .max(20, { message: "Password must not exceed 20 characters." })
  .refine((password) => /[A-Z]/.test(password), {
    message: "Password must contain at least one uppercase letter.",
  })
  .refine((password) => /[a-z]/.test(password), {
    message: "Password must contain at least one lowercase letter.",
  })
  .refine((password) => /[0-9]/.test(password), {
    message: "Password must contain at least one number.",
  })
  .refine((password) => /[!@#$%^&*?_]/.test(password), {
    message: "Password must contain at least one special character.",
  });

export const editAccountFinancialBasicSchema = z.object({
  quickbooksId: z.string(),
  purchaseOrder: z.string(),
});

export const changePasswordSchema = (
  isOldPasswordRequired: boolean,
  isUserNameRequired: boolean,
) =>
  z
    .object({
      userName: isUserNameRequired
        ? z.string().min(1, "This field is required")
        : z.string().optional(),
      oldPassword: isOldPasswordRequired
        ? z.string().min(1, "This field is required")
        : z.string().optional(),
      newPassword: passwordSchema,
      confirmNewPassword: z.string().min(1, "This field is required"),
    })
    .refine((data) => data.newPassword === data.confirmNewPassword, {
      message: "Passwords do not match",
      path: ["confirmNewPassword"],
    });

export const forgotPasswordSchema = z.object({
  userName: z.string().trim().min(1, "Username is required"),
});

export const createAccountFinancialBasicSchema = z.object({
  quickbooksId: z.string(),
  purchaseOrder: z.string(),
  invoiceFrequency: z
    .string()
    .min(1, "This field is required")
    .refine((value) => value !== "select", {
      message: "This field is required",
    }),
});

export const editOrganizationProfileHeaderSchema = z.object({
  name: z.string().trim().min(1, "This field is required"),
  country: z.string().min(1, "This field is required"),
  logo: z.any().optional(),
});

export const editOrganizationProfileBodySchema = z.object({
  contactPerson: z.string().trim().min(1, "This field is required"),
  contactNumber: phoneNumberSchema,
  officeNumber: phoneNumberSchema,
  mobileNumber: phoneNumberSchema,
  timezoneId: z.number().min(1, "This field is required"),
});

export const editOrganizationPaymentReminderSchema = z.object({
  paymentReminder: z.union([z.string(), z.number()]),
  frequencyDays: z.number().min(1, "This field is required"),
});

export const editQuickbooksIntegrationSchema = z.object({
  clientId: z.string().trim().min(1, "This field is required"),
  realmId: z.string().trim().min(1, "This field is required"),
  secretId: z.string().trim().min(1, "This field is required"),
});

export const editPaymentGatewayIntegrationSchema = z.object({
  wpAcceptorId: z.string().trim().min(1, "This field is required"),
  wpAccountId: z.string().trim().min(1, "This field is required"),
  wpAccountToken: z.string().trim().min(1, "This field is required"),
  wpTerminalId: z.string().trim().min(1, "This field is required"),
});

export const getEditOrganizationAddressSchema = (isCanada: boolean) => {
  return z.object({
    instruction: z.string().trim().optional(),
    email: z.string().email().optional(),
    website: urlSchema,
    addressLine1: z.string().trim().min(1, "This field is required"),
    addressLine2: z.string().trim().optional(),
    city: z.string().trim().min(1, "This field is required"),
    state: z.string().min(1, "This field is required"),
    zip: isCanada ? CanadaPostalCodeSchema : USPostalCodeSchema,
  });
};

export const refundAmountSchema = z.object({
  refundAmount: z
    .union([
      z.number({ invalid_type_error: "This field is required" }),
      z.string(),
    ])
    .transform((val) => Number(val))
    .refine((val) => !isNaN(val) && val > 0, {
      message: "Enter a valid amount",
    }),
});

export const discountSchema = z
  .object({
    discountAmount: z
      .union([
        z.number({ invalid_type_error: "This field is required" }),
        z.string(),
      ])
      .transform((val) => Number(val))
      .optional()
      .refine((val) => val === undefined || (!isNaN(val) && val >= 0), {
        message: "Enter a valid discount amount",
      }),
    discountNote: z
      .string()
      .trim()
      .max(200, { message: "Cannot exceed 200 characters." })
      .optional(),
    feeAmount: z
      .union([
        z.number({ invalid_type_error: "This field is required" }),
        z.string(),
      ])
      .transform((val) => Number(val))
      .optional()
      .refine((val) => val === undefined || (!isNaN(val) && val >= 0), {
        message: "Enter a valid fee amount",
      }),
    feeNote: z
      .string()
      .trim()
      .max(200, { message: "Cannot exceed 200 characters." })
      .optional(),
    commonError: z.string().optional(),
  })
  .refine(
    (data) =>
      (data.discountAmount && data.discountAmount > 0) ||
      (data.feeAmount && data.feeAmount > 0),
    {
      message: "Enter either a discount or a fee.",
      path: ["commonError"],
    },
  )
  .refine(
    (data) =>
      !data.discountAmount?.toString().split(".")[1] ||
      data.discountAmount.toString().split(".")[1].length <= 2,
    {
      message: "Enter valid amount with up to 2 decimals",
      path: ["discountAmount"],
    },
  )
  .refine(
    (data) =>
      !data.feeAmount?.toString().split(".")[1] ||
      data.feeAmount.toString().split(".")[1].length <= 2,
    {
      message: "Enter valid amount with up to 2 decimals",
      path: ["feeAmount"],
    },
  );
export const CreateOneOffInvoiceSchema = z.object({
  unitPrice: z
    .string()
    .min(1, "Unit price is required")
    .refine((value) => !isNaN(Number(value)), {
      message: "must be a number",
    }),

  quantity: z
    .string()
    .min(1, "Quantity is required")
    .refine((value) => !isNaN(Number(value)), {
      message: "must be a number",
    })
    .refine((value) => Number(value) > 0, {
      message: "must be greater than 0",
    })
    .refine((value) => /^\d+(\.\d{1,2})?$/.test(value), {
      message: "max 2 decimal places allowed",
    }),
});
export const multipleCheckPaymentSchema = z.object({
  checkNumber: z
    .string()
    .trim()
    .min(1, "This field is required")
    .max(50, { message: "Cannot exceed 50 characters." }),
  bankName: z
    .string()
    .trim()
    .min(1, "This field is required")
    .max(100, { message: "Cannot exceed 100 characters." }),
  amount: z.string().regex(/^\d+(\.\d{1,2})?$/, "Enter a valid amount"),
});

export const getPaymentDetailsSchema = (
  isCanada: boolean,
  amount?: number,
  paymentMethod?: PaymentMethod,
  paymentSource?: PaymentSource,
) => {
  const baseSchema = {
    amount: amount
      ? z
          .number({
            required_error: "This field is required",
            invalid_type_error: "This field is required",
          })
          .min(0)
          .max(amount, `Cannot exceed $ ${amount}`)
          .refine((val) => !isNaN(val) && val > 0, {
            message: "Enter a valid amount",
          })
          .refine(
            (val) =>
              !val.toString().split(".")[1] ||
              val.toString().split(".")[1].length <= 2,
            {
              message: `Enter a valid amount with up to 2 decimal places`,
            },
          )
      : z
          .number({
            required_error: "This field is required",
            invalid_type_error: "This field is required",
          })
          .min(0),
    paymentMethod: z.string(),
  };

  const checkSchema = {
    ...baseSchema,
    bankName: z
      .string()
      .trim()
      .min(1, "This field is required")
      .max(100, { message: "Cannot exceed 100 characters." }),
    checkNumber: z
      .string()
      .trim()
      .min(1, "This field is required")
      .max(50, { message: "Cannot exceed 50 characters." }),
  };

  const savedPaymentSchema = {
    ...baseSchema,
    omnitokenId: z.union([z.string(), z.number()]).optional(),
    tokenId: z.string().optional(),
    tokenType: z.string().optional(),
    netTransactionId: z.union([z.string(), z.undefined()]).optional(),
    expirationMonth: z.string().nullable().optional(),
    expirationYear: z.string().nullable().optional(),
    cardLogo: z.string().optional(),
  };

  const newCardOrAchSchema = {
    ...baseSchema,
    addressLine1: z.string().trim().min(1, "This field is required"),
    addressLine2: z.string().trim().optional(),
    city: z.string().trim().min(1, "This field is required"),
    state: z.string().min(1, "This field is required"),
    zip: isCanada ? CanadaPostalCodeSchema : USPostalCodeSchema,
    omnitokenId: z.union([z.string(), z.number()]).optional(),
    tokenId: z.string().optional(),
    tokenType: z.string().optional(),
    netTransactionId: z.union([z.string(), z.undefined()]).optional(),
    expirationMonth: z.string().optional(),
    expirationYear: z.string().optional(),
    cardLogo: z.string().optional(),
    saveCard: z.boolean(),
  };

  return (() => {
    if (
      paymentSource === PaymentSource.Saved &&
      (paymentMethod === PaymentMethod.Card ||
        paymentMethod === PaymentMethod.Ach)
    ) {
      return z.object(savedPaymentSchema);
    }

    switch (paymentMethod) {
      case PaymentMethod.Card:
      case PaymentMethod.Ach:
        return z.object(newCardOrAchSchema);
      case PaymentMethod.Check:
        return z.object(checkSchema);
      default:
        return z.object(baseSchema);
    }
  })();
};
